import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DOCS_PATH } from '../../config/paths';
import DashboardThemeToggle from '../dashboard/DashboardThemeToggle';
import TestnetFaucetLink from '../nav/TestnetFaucetLink';
import { cronpayLogoSrc } from '../../config/brandAssets';
import { useTheme } from '../../context/ThemeContext';
import { LANDING_NAV_LINKS } from './landingConfig';
import { isTestnetMode } from '../../constant/constant';

const landingNavLinks = LANDING_NAV_LINKS.filter(
    (item) => item.id !== 'try-testnet' || isTestnetMode,
);

const navBtnClass =
    'touch-manipulation inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-colors active:scale-[0.98] sm:min-h-[44px] sm:py-2.5';

const scrollToId = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const LandingHeader = ({ onLaunch, onOpenJobs }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { resolvedTheme } = useTheme();
    const logoSrc = cronpayLogoSrc(resolvedTheme);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = menuOpen ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [menuOpen]);

    const closeMenu = () => setMenuOpen(false);

    const handleLaunch = () => {
        closeMenu();
        onLaunch?.();
    };

    const handleJobs = () => {
        closeMenu();
        onOpenJobs?.();
    };

    const handleScroll = (id) => {
        closeMenu();
        scrollToId(id);
    };

    return (
        <>
            <header
                className={`landing-header relative sticky top-0 z-50 w-full transition-[box-shadow,border-color,background-color] duration-300 ${
                    scrolled
                        ? 'border-b border-zinc-200/90 bg-white shadow-sm shadow-zinc-900/5 backdrop-blur-lg dark:border-zinc-800/90 dark:bg-zinc-950 dark:shadow-black/20'
                        : 'border-b border-zinc-200/80 bg-white backdrop-blur-md dark:border-zinc-800/80 dark:bg-zinc-950'
                }`}
            >
                <div className="mx-auto flex h-14 max-w-6xl items-center justify-between gap-3 px-4 sm:h-16 sm:px-6 lg:px-8">
                    <Link
                        to="/"
                        className="inline-flex shrink-0 items-center"
                        onClick={closeMenu}
                    >
                        <img
                            src={logoSrc}
                            alt="CronPay"
                            className="block h-8 w-auto object-contain sm:h-9"
                        />
                    </Link>

                    <nav
                        className="hidden items-center gap-0.5 md:flex"
                        aria-label="Landing"
                    >
                        {landingNavLinks.map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                onClick={() => scrollToId(item.id)}
                                className="rounded-lg px-3 py-2 text-sm font-medium text-zinc-600 transition-colors hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800/80 dark:hover:text-white"
                            >
                                {item.label}
                            </button>
                        ))}
                        <Link
                            to={`${DOCS_PATH}/welcome`}
                            className="rounded-lg px-3 py-2 text-sm font-medium text-zinc-600 transition-colors hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800/80 dark:hover:text-white"
                        >
                            Docs
                        </Link>
                    </nav>

                    <div className="flex items-center gap-1.5 sm:gap-2">
                        <div className="hidden sm:block">
                            <TestnetFaucetLink compact />
                        </div>
                        <DashboardThemeToggle />
                        <button
                            type="button"
                            onClick={handleLaunch}
                            className="hidden rounded-xl bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-500 active:scale-[0.98] sm:inline-flex"
                        >
                            Launch app
                        </button>
                        <button
                            type="button"
                            className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-zinc-600 transition-colors hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800 md:hidden"
                            onClick={() => setMenuOpen((o) => !o)}
                            aria-expanded={menuOpen}
                            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                        >
                            <i
                                className={`fa-solid ${menuOpen ? 'fa-xmark' : 'fa-bars'} text-lg`}
                            />
                        </button>
                    </div>
                </div>
            </header>

            <div
                className={`fixed inset-0 z-[60] md:hidden ${menuOpen ? 'visible' : 'invisible'}`}
                aria-hidden={!menuOpen}
            >
                <button
                    type="button"
                    className={`absolute inset-0 bg-zinc-900/60 backdrop-blur-sm transition-opacity duration-300 ${menuOpen ? 'opacity-100' : 'opacity-0'}`}
                    onClick={closeMenu}
                    aria-label="Close menu"
                />
                <div
                    className={`absolute right-0 top-0 flex h-full w-[min(100%,320px)] flex-col border-l border-zinc-200 bg-white shadow-2xl transition-transform duration-300 ease-out dark:border-zinc-800 dark:bg-zinc-950 ${menuOpen ? 'translate-x-0' : 'translate-x-full'}`}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Menu"
                >
                    <div className="flex items-center justify-between border-b border-zinc-100 px-4 py-4 dark:border-zinc-800">
                        <img src={logoSrc} alt="" className="h-8 w-auto object-contain" />
                        <button
                            type="button"
                            onClick={closeMenu}
                            className="flex h-10 w-10 items-center justify-center rounded-lg text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                        >
                            <i className="fa-solid fa-xmark text-lg" />
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto px-4 py-5">
                        <div className="mb-4">
                            <TestnetFaucetLink className="w-full justify-center py-2.5" />
                        </div>
                        <div className="flex flex-col gap-2">
                            <button
                                type="button"
                                onClick={handleLaunch}
                                className={`${navBtnClass} bg-teal-600 text-white hover:bg-teal-700`}
                            >
                                Launch app
                                <i className="fa-solid fa-arrow-right text-xs" />
                            </button>
                            <button
                                type="button"
                                onClick={handleJobs}
                                className={`${navBtnClass} border border-teal-300 bg-teal-50 text-teal-900 dark:border-teal-700 dark:bg-teal-950/50 dark:text-teal-200`}
                            >
                                Company &amp; Job
                            </button>
                            {landingNavLinks.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => handleScroll(item.id)}
                                    className={`${navBtnClass} border border-zinc-200 bg-zinc-50 text-zinc-800 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200`}
                                >
                                    {item.label}
                                </button>
                            ))}
                            <Link
                                to={`${DOCS_PATH}/welcome`}
                                onClick={closeMenu}
                                className={`${navBtnClass} border border-zinc-200 bg-white text-zinc-800 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200`}
                            >
                                Documentation
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default LandingHeader;
